import type { Session } from "@supabase/supabase-js";

import { accountRoleForSession, resolveAccountRole } from "./account-role";
import { consoleChunks, prefetchProviderSection, providerSectionChunks } from "./console-loaders";
import { restoreDashboardLocation } from "./dashboard-location";
import { onDashboardResourceCacheCleared, prefetchDashboardResource } from "./dashboard-resource";
import { readCachedProviderAccess } from "./provider-access-cache";
import {
  getLegalStatus, getPlatformStatus, getProviderApplicationStatus, listProviderAnnouncements, type ProviderApplicationStatusResponse
} from "./tinkr-api";
import { providerShopStatusFetcher } from "./tinkr-api/provider-shop-status";

/**
 * Console bootstrap (P-01).
 *
 * Before: ConsoleSurface chunk → legal-status → provider/application → Home
 * chunk → Home data, five round trips one after another, each started by the
 * previous one's render. Now the moment a session is known for the dashboard,
 * all of them start together. Rendering is unchanged: LegalAcceptanceGate
 * still shows nothing of the dashboard until legal status resolves (fail
 * closed); AccessDashboard still waits for the provider access read. Only the
 * requests overlap.
 *
 * Results live in memory for the signed-in account only. The one-shot
 * prefetches are taken by the first reader within PREFETCH_MAX_AGE_MS and
 * dropped on sign-out or account switch together with the dashboard cache.
 */
export const PREFETCH_MAX_AGE_MS = 30_000;
type Prefetch = { owner: string; startedAt: number; promise: Promise<unknown>; settled?: { value: unknown } };
const prefetches = new Map<string, Prefetch>();
let bootstrappedFor: string | null = null;
onDashboardResourceCacheCleared(() => {
  prefetches.clear();
  bootstrappedFor = null;
});

export type ConsolePrefetchName = "legal-status" | "platform-status" | "provider-application";

function start<T>(name: ConsolePrefetchName, owner: string, run: () => Promise<T>): Promise<T> {
  const promise = run();
  const entry: Prefetch = { owner, startedAt: Date.now(), promise };
  promise.then((value) => { entry.settled = { value }; }, () => undefined);
  prefetches.set(name, entry);
  return promise;
}

/** Hands a started read to its first consumer (one shot). Null when there is
 * none for this account, or it is too old to stand in for a fresh read. */
export function takeConsolePrefetch<T>(name: ConsolePrefetchName, owner: string): Promise<T> | null {
  const entry = prefetches.get(name);
  if (!entry) return null;
  prefetches.delete(name);
  if (entry.owner !== owner || Date.now() - entry.startedAt > PREFETCH_MAX_AGE_MS) return null;
  return entry.promise as Promise<T>;
}

/** A prefetch that already succeeded, without taking it (lets a component
 * render its first frame with the value instead of inserting it later). */
export function peekSettledConsolePrefetch<T>(name: ConsolePrefetchName, owner: string): T | undefined {
  const entry = prefetches.get(name);
  if (!entry || entry.owner !== owner || Date.now() - entry.startedAt > PREFETCH_MAX_AGE_MS) return undefined;
  return entry.settled?.value as T | undefined;
}

/** The provider section the console will open: the URL first, then the
 * remembered location, then Home (same order as OperationsDashboard). */
export function initialProviderSection(userId: string): string {
  const sections = Object.keys(providerSectionChunks);
  if (typeof window !== "undefined") {
    const requested = new URLSearchParams(window.location.search).get("section");
    if (requested) return sections.includes(requested) ? requested : "dashboard";
  }
  return restoreDashboardLocation(userId, "provider", sections)?.section ?? "dashboard";
}

export function startConsoleBootstrap(session: Session, locale: string): void {
  const uid = session.user.id;
  if (bootstrappedFor === uid) return;
  bootstrappedFor = uid;
  void consoleChunks.surface().catch(() => undefined);
  start("legal-status", uid, () => getLegalStatus(session));
  start("platform-status", uid, () => getPlatformStatus(session));
  const resolved = resolveAccountRole(session);
  if (resolved && !resolved.ok) return;
  const role = accountRoleForSession(session);
  if (role === "admin" || role === "partner") return;

  const access = start("provider-application", uid, () => getProviderApplicationStatus(session));
  // The chrome's own reads (the side-nav shop chip and the operational
  // notices) go out now, for every provider console, not after the access
  // read: the chrome's first frame then already has them, so a notice is not
  // inserted above Home and pushes it down later (P-04). Same entries and
  // fetchers as the chrome's hooks.
  void prefetchDashboardResource(`warm-shop-status:provider:${uid}`, providerShopStatusFetcher(session));
  void prefetchDashboardResource(`provider-announcements:${uid}:${locale}`, () => listProviderAnnouncements(session, locale), 300_000);
  const section = initialProviderSection(uid);
  prefetchProviderSection(section);
  // Home's own reads only when the console opens on Home.
  if (section !== "dashboard") return;
  const startHome = () => {
    void import("./console-home-prefetch")
      .then(({ prefetchProviderHome }) => {
        // Signed out or switched account while the module loaded.
        if (bootstrappedFor === uid) prefetchProviderHome(session);
      })
      .catch(() => undefined);
  };
  // Home's reads need a provider record and a console that will show Home. A
  // returning provider's access hint says so at once; on a first visit on this
  // device the access read started above says so one round trip later, still
  // before the console has rendered.
  if (rendersProviderHome(readCachedProviderAccess(uid))) {
    startHome();
    return;
  }
  access.then((status) => {
    if (bootstrappedFor === uid && rendersProviderHome(status)) startHome();
  }, () => undefined);
}

/** Whether AccessDashboard will render Home's panels for this access status:
 * a provider record exists and it is not waiting for review (mirrors
 * AccessDashboard's `isProviderUnderReview`). Only decides what to prefetch. */
function rendersProviderHome(status: ProviderApplicationStatusResponse | null | undefined): boolean {
  if (status?.gate?.status !== "provider_created") return false;
  return !(status.provider?.submittedForReviewAt && status.provider.operationalStatus !== "active");
}
